import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { searchCustomer } from "src/apis/customerApi";
import { DropDownForm } from "src/components/common/form/DropdownForm";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import useUiuxStore from "src/store/useUiuxStore";

import { BRDiv, inputFormHeightOption } from "src/components/common/common";
import { CodeForm } from "src/components/common/form/CodeForm";
import { InputForm } from "src/components/common/form/input/InputForm";
import { SingleDateForm } from "src/components/common/form/SingleDateForm";
import { KSK1030_R01HeaderDto } from "./Dto_KSK1030_R01";
import { useKSK1030_R01Store } from "./Store_KSK1030_R01";
import { formatDate } from "src/utils/string/stringUtil";
import { SysCodeDto } from "src/dtos/code/sysCodeDto";
import { getEmployeeList } from "src/apis/employeeApi";
import { useInfoFormEvents } from "src/hooks/useInfoFormEvents";
import { InfoCodeSearchFieldConfig, InfoFieldHandlers } from "src/types/info-form";


interface Props {
	editable: boolean;
	defaultKSK1030_R01HeaderRow: KSK1030_R01HeaderDto;
	handleDetailInfoCode: (headerData: KSK1030_R01HeaderDto) => void;
	handleFetchDetailList: (wmsOrderNo: string, warehouseCd: string, isNewSelection: boolean, previousOrderNo: string | undefined) => void;
	getWmsOrderFieldHandlers: (fieldName: string) => InfoFieldHandlers;
	onSearchClick: () => void;
}

export const Info_KSK1030_R01 = (props: Props) => {
	const {
		defaultKSK1030_R01HeaderRow,
		handleFetchDetailList,
		getWmsOrderFieldHandlers,
		onSearchClick,
	} = props;

	const { inoutClassList, inoutMethodList, wmsCdList, compCdList } = useCommonCodeStore();
	const { setCodeSearchModalProps } = useUiuxStore();
	const {
		editKSK1030_R01, setEditKSK1030_R01,
		transportComplete,
		warehouseCdListToUseList,
		setDetailFetchTrigger,
	} = useKSK1030_R01Store();

	const custInfoHandleFoucsId = 'infoCustInput';
	const empInfoHandleFoucsId = 'infoEmpInput';
	const wmsOrderInfoHandleFocusId = 'inputWmsOrderNo_search';

	const infoFieldConfigs: InfoCodeSearchFieldConfig[] = [
		{
			fieldName: 'customCd',
			category: '거래처',
			focusId: custInfoHandleFoucsId,
			responseMapping: {
				customCd: 'customCd',
				customNm: 'customNm',
				telNo: 'telNo',
				faxNo: 'faxNo',
				moneyCd: 'moneyCd',
				taxEmail: 'email',
				post: 'post',
				address: 'address',
				detailAddress: 'otherAddress',
			},
			searchFunction: async (searchText: string) => {
				const customerResponse = await searchCustomer(searchText);
				return customerResponse.data.data.find(value => value.customCd === searchText);
			},
			extraFilter: () => ({
				customCd: editKSK1030_R01?.customCd || '',
				customNm: editKSK1030_R01?.customNm || '',
			}),
		},
		{
			fieldName: 'empNo',
			category: '담당자',
			focusId: empInfoHandleFoucsId,
			responseMapping: {
				empNo: 'empNo',
				empNm: 'empNm',
			},
			searchFunction: async (code: string) => {
				const employeeResponse = await getEmployeeList('all');
				return employeeResponse.data.data.find(value => value.empNo === code);
			},
			extraFilter: () => ({
				empNo: editKSK1030_R01?.empNo || '',
				empNm: editKSK1030_R01?.empNm || '',
			}),
		},
	];

	const { getFieldHandlers } = useInfoFormEvents({
		editData: editKSK1030_R01,
		setEditData: setEditKSK1030_R01,
		defaultData: defaultKSK1030_R01HeaderRow,
		infoFieldConfigs,
		setCodeSearchModalProps,
		formName: 'KSK1030_R01',
	});

	const [orderPickerVisible, setOrderPickerVisible] = useState(false);
	const [orderPickerPos, setOrderPickerPos] = useState<{ top: number; left: number; height: number }>({ top: 0, left: 0, height: 0 });
	const [deadlinePickerVisible, setDeadlinePickerVisible] = useState(false);
	const [deadlinePickerPos, setDeadlinePickerPos] = useState<{ top: number; left: number; height: number }>({ top: 0, left: 0, height: 0 });

	// 창고 변경 시 출고 목록·재고 LOT 재조회
	useEffect(() => {
		if (editKSK1030_R01?.warehouseCd && editKSK1030_R01.wmsOrderNo) {
			setDetailFetchTrigger(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- warehouse 변경만 감지
	}, [editKSK1030_R01?.warehouseCd]);

	const handleDateChange = useCallback((field: keyof KSK1030_R01HeaderDto, value: any) => {
		setEditKSK1030_R01({
			...defaultKSK1030_R01HeaderRow,
			...editKSK1030_R01,
			[field]: value,
		});
	}, [editKSK1030_R01, defaultKSK1030_R01HeaderRow, setEditKSK1030_R01]);

	const handleChange = useCallback((e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>) => {
		const { name, value } = e.target;

		let additionalUpdates = {};
		if (name === 'logicCenter') {
			const availableWarehouses = warehouseCdListToUseList(value);
			additionalUpdates = {
				warehouseCd: availableWarehouses[0]?.warehouseCd || '',
			};
		}

		setEditKSK1030_R01({
			...defaultKSK1030_R01HeaderRow,
			...editKSK1030_R01,
			[name]: value,
			...additionalUpdates,
		});
	}, [editKSK1030_R01, defaultKSK1030_R01HeaderRow, warehouseCdListToUseList, setEditKSK1030_R01]);

	return (
		<div className="w-full h-full flex flex-wrap content-start overflow-y-auto">
			<div className="w-full flex flex-wrap items-center gap-[8px]">
				<CodeForm
					label="출고지시번호"
					value={editKSK1030_R01?.wmsOrderNo || ""}
					custNmValue=""
					editable={true}
					custNmInputless={true}
					handleFoucsId={wmsOrderInfoHandleFocusId}
					name="wmsOrderNo"
					handleChange={getWmsOrderFieldHandlers('wmsOrderNo').onChange}
					onDoubleClick={getWmsOrderFieldHandlers('wmsOrderNo').onDoubleClick}
					onKeyDown={getWmsOrderFieldHandlers('wmsOrderNo').onEnterKeyDown}
					wrapperOption={{
						wrapperStyle: "w-[400px] min-w-[400px] mb-[4px]",
						titleStyle: "w-[150px] min-w-[150px] text-left",
						codeInputStyle: "w-[240px] min-w-[240px] " + inputFormHeightOption,
						custNmInputStyle: "w-[105px] min-w-[105px]",
					}}
				/>
				<button
					className={`text-white bg-blue-dark items-center justify-center px-[8px] py-[2px] rounded-[4px] w-[74px] mb-[4px] flex-shrink-0 ${inputFormHeightOption}`}
					onClick={onSearchClick}
				>
					조회
				</button>
			</div>
			<BRDiv />

			<DropDownForm
				title="사업장"
				name="compCd"
				initLabelLess={true}
				value={editKSK1030_R01?.compCd}
				data={compCdList}
				editable={!transportComplete}
				onChange={handleChange}
			/>
			<SingleDateForm
				title="출고일자"
				value={editKSK1030_R01?.orderDt ? new Date(editKSK1030_R01.orderDt) : undefined}
				setValue={(value: Date | undefined) => handleDateChange('orderDt', formatDate(value))}
				editable={true}
				visible={orderPickerVisible} setVisible={setOrderPickerVisible}
				onChange={handleChange}
				pickerPos={orderPickerPos} setPickerPos={setOrderPickerPos}
			/>
			<BRDiv />

			<DropDownForm
				title="출고구분"
				name="inoutClass"
				initLabelLess={false}
				value={editKSK1030_R01?.inoutClass || '230'}
				data={inoutClassList}
				editable={false}
				onChange={handleChange}
			/>
			<DropDownForm
				title="배송방법"
				name="inoutMethod"
				initLabelLess={true}
				value={editKSK1030_R01?.inoutMethod}
				data={inoutMethodList}
				editable={!transportComplete}
				onChange={handleChange}
			/>
			<BRDiv />

			<CodeForm
				label="거래처"
				value={editKSK1030_R01?.customCd || ""}
				custNmValue={editKSK1030_R01?.customNm || ""}
				editable={false}
				handleFoucsId={custInfoHandleFoucsId}
				name="customCd"
				handleChange={getFieldHandlers('customCd').onChange}
				onDoubleClick={getFieldHandlers('customCd').onDoubleClick}
				onKeyDown={getFieldHandlers('customCd').onEnterKeyDown}
			/>
			<CodeForm
				label="출고담당자"
				value={editKSK1030_R01?.empNo || ""}
				custNmValue={editKSK1030_R01?.empNm || ""}
				editable={!transportComplete}
				handleFoucsId={empInfoHandleFoucsId}
				name="empNo"
				handleChange={getFieldHandlers('empNo').onChange}
				onDoubleClick={getFieldHandlers('empNo').onDoubleClick}
				onKeyDown={getFieldHandlers('empNo').onEnterKeyDown}
			/>
			<BRDiv />

			<DropDownForm<SysCodeDto>
				title="출고물류"
				data={wmsCdList}
				value={editKSK1030_R01?.logicCenter || ""}
				name="logicCenter"
				editable={editKSK1030_R01?.closeTag === 'N'}
				initLabelLess={true}
				onChange={handleChange}
			/>
			<DropDownForm
				title="창고번호"
				name="warehouseCd"
				initLabelLess={true}
				value={editKSK1030_R01?.warehouseCd}
				data={warehouseCdListToUseList(editKSK1030_R01?.logicCenter || '')}
				editable={editKSK1030_R01?.closeTag === 'N'}
				onChange={handleChange}
			/>
			<BRDiv />

			<InputForm
				title="수주번호"
				value={editKSK1030_R01?.salesOrderNo || ""}
				editable={false}
				name="salesOrderNo"
				onChange={handleChange}
			/>
			<SingleDateForm
				title="납기일자"
				value={editKSK1030_R01?.deadlineDt ? new Date(editKSK1030_R01.deadlineDt) : undefined}
				setValue={(value: Date | undefined) => handleDateChange('deadlineDt', formatDate(value))}
				editable={!transportComplete}
				visible={deadlinePickerVisible} setVisible={setDeadlinePickerVisible}
				onChange={handleChange}
				pickerPos={deadlinePickerPos} setPickerPos={setDeadlinePickerPos}
			/>
			<BRDiv />

			<InputForm
				title="비고"
				value={editKSK1030_R01?.remark || ""}
				editable={!transportComplete}
				name="remark"
				wrapperOption={{
					wrapperStyle: "w-[510px] min-w-[510px] mr-[10px] mb-[5px]",
					titleStyle: "w-[150px] min-w-[150px] text-left",
					inputStyle: "w-full min-w-full",
				}}
				onChange={handleChange}
			/>
		</div>
	);
};
